import { CalendarClock, Send, Trash2 } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { BulkAction } from '@nucleus-commerce/admin-kit/index-table';
import type { SocialPost } from '@nucleus-commerce/api-client/admin';

export interface PostBulkHandlers {
  canWrite: boolean;
  onPublish: (rows: SocialPost[]) => Promise<void>;
  onSchedule: (rows: SocialPost[]) => void;
  onDiscard: (rows: SocialPost[]) => Promise<void>;
}

/** Publish now, schedule (opens the dialog) and discard, on the selected loaded rows. */
export function usePostBulkActions({
  canWrite,
  onPublish,
  onSchedule,
  onDiscard,
}: PostBulkHandlers): Array<BulkAction<SocialPost>> {
  const { t } = useTranslation('social');
  return useMemo(() => {
    if (!canWrite) return [];
    return [
      {
        id: 'publish',
        label: t('bulk.publish'),
        icon: <Send aria-hidden />,
        promoted: true,
        onAction: (_selection, rows) => onPublish(rows),
      },
      {
        id: 'schedule',
        label: t('bulk.schedule'),
        icon: <CalendarClock aria-hidden />,
        promoted: true,
        keepSelection: true,
        onAction: (_selection, rows) => onSchedule(rows),
      },
      {
        id: 'discard',
        label: t('bulk.discard'),
        icon: <Trash2 aria-hidden />,
        variant: 'destructive',
        onAction: (_selection, rows) => onDiscard(rows),
      },
    ];
  }, [canWrite, onDiscard, onPublish, onSchedule, t]);
}
